import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { SUPPLIER_CATEGORIES } from '../constants.js';
import { SupplierDetail } from './SupplierDetail.js';
import { Check, Pencil, Trash2 } from 'lucide-react';
import { Callout, PageHeader } from '../ui/index.js';

type SupplierRow = Database['public']['Tables']['suppliers']['Row'];

const CATEGORIES = SUPPLIER_CATEGORIES;
type Category = (typeof CATEGORIES)[number];

interface SupplierForm {
  display_name: string;
  tin: string;
  contact_person: string;
  phone: string;
  category: Category;
}

const BLANK: SupplierForm = {
  display_name: '',
  tin: '',
  contact_person: '',
  phone: '',
  category: 'other',
};

function formFromRow(row: SupplierRow): SupplierForm {
  return {
    display_name: row.display_name,
    tin: row.tin,
    contact_person: row.contact_person ?? '',
    phone: row.phone ?? '',
    category: (CATEGORIES as readonly string[]).includes(row.category)
      ? (row.category as Category)
      : 'other',
  };
}

export function SuppliersManager({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();

  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // modal state: null = closed, 'create' = new supplier, rowId = edit existing
  const [modal, setModal] = useState<'create' | string | null>(null);
  const [form, setForm] = useState<SupplierForm>(BLANK);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // detail view: supplier id whose purchase summary is open
  const [detailId, setDetailId] = useState<string | null>(null);
  const deepLinkConsumed = useRef(false);

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('suppliers')
      .select('*')
      .order('display_name');
    if (err) setError(err.message);
    else setSuppliers((data ?? []) as SupplierRow[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  // Deep link: #/suppliers?tin=… opens the supplier detail directly.
  useEffect(() => {
    if (loading || deepLinkConsumed.current) return;
    deepLinkConsumed.current = true;
    const tin = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('tin');
    if (!tin) return;
    const match = suppliers.find((s) => s.tin === tin);
    if (match) setDetailId(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  function openDetail(row: SupplierRow) {
    setDetailId(row.id);
    window.history.replaceState(null, '', `#/suppliers?tin=${encodeURIComponent(row.tin)}`);
  }

  function closeDetail() {
    setDetailId(null);
    window.history.replaceState(null, '', '#/suppliers');
  }

  function openCreate() {
    setForm(BLANK);
    setModal('create');
    setError(null);
  }

  function openEdit(row: SupplierRow) {
    setForm(formFromRow(row));
    setModal(row.id);
    setError(null);
  }

  function closeModal() {
    setModal(null);
    setError(null);
  }

  async function handleSave() {
    if (!supabase || !clientId) return;
    if (!form.display_name.trim()) {
      setError(t('suppliers.nameRequired'));
      return;
    }
    if (modal === 'create' && !form.tin.trim()) {
      setError(t('suppliers.tinRequired'));
      return;
    }
    setSaving(true);
    setError(null);

    if (modal === 'create') {
      const { error: err } = await supabase.from('suppliers').insert({
        client_id: clientId,
        tin: form.tin.trim(),
        display_name: form.display_name.trim(),
        category: form.category,
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
      });
      if (err) {
        setError(err.message);
        setSaving(false);
        return;
      }
    } else {
      // editing existing — TIN and legal name are immutable
      const { error: err } = await supabase
        .from('suppliers')
        .update({
          display_name: form.display_name.trim(),
          category: form.category,
          contact_person: form.contact_person.trim() || null,
          phone: form.phone.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', modal!);
      if (err) {
        setError(err.message);
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    closeModal();
    void load();
  }

  async function handleDelete(id: string) {
    if (!supabase) return;
    const { error: err } = await supabase.from('suppliers').delete().eq('id', id);
    if (err) setError(err.message);
    else {
      setDeleteConfirmId(null);
      void load();
    }
  }

  const editingRow = modal && modal !== 'create'
    ? suppliers.find((s) => s.id === modal) ?? null
    : null;

  const filtered = suppliers.filter((s) => {
    const q = search.toLowerCase();
    return (
      s.display_name.toLowerCase().includes(q) ||
      s.tin.includes(q) ||
      (s.name ?? '').toLowerCase().includes(q) ||
      (s.contact_person ?? '').toLowerCase().includes(q)
    );
  });

  const detailRow = detailId ? (suppliers.find((s) => s.id === detailId) ?? null) : null;
  if (detailRow) {
    return <SupplierDetail clientId={clientId} supplier={detailRow} onBack={closeDetail} />;
  }

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('suppliers.title')}
        subtitle={t('suppliers.subtitle')}
        actions={
          <button className="btn" onClick={openCreate}>
            + {t('suppliers.addSupplier')}
          </button>
        }
      />

      {error && !modal && <Callout tone="danger">{error}</Callout>}

      <div className="panel">
        <div className="add-form" style={{ marginBottom: 8 }}>
          <input
            type="search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            {search ? t('suppliers.noMatch') : t('suppliers.noSuppliers')}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('suppliers.colName')}</th>
                <th>{t('suppliers.colLegalName')}</th>
                <th>{t('suppliers.colTin')}</th>
                <th>{t('suppliers.colCategory')}</th>
                <th>{t('suppliers.colContact')}</th>
                <th>{t('suppliers.colPhone')}</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id}>
                  <td>
                    <button
                      className="link"
                      onClick={() => openDetail(s)}
                      title={t('suppliers.openSummary')}
                    >
                      {s.display_name || <span style={{ color: 'var(--text-subtle)' }}>—</span>}
                    </button>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {s.name || <span style={{ color: 'var(--text-subtle)' }}>—</span>}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{s.tin}</td>
                  <td>
                    <span className="badge">{s.category}</span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {s.contact_person || '—'}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {s.phone || '—'}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="btn-icon"
                        onClick={() => openEdit(s)}
                        title={t('common.save')}
                      >
                        <Pencil className="ui-i" />
                      </button>
                      {deleteConfirmId === s.id ? (
                        <>
                          <button
                            className="btn-icon danger"
                            onClick={() => void handleDelete(s.id)}
                            title={t('common.confirmDelete')}
                          >
                            <Check className="ui-i" />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => setDeleteConfirmId(null)}
                            title={t('common.cancel')}
                          >
                            ×
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-icon danger"
                          onClick={() => setDeleteConfirmId(s.id)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="ui-i" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modal === 'create' ? t('suppliers.addSupplier') : t('suppliers.editSupplier')}
              </h2>
              <button className="btn-icon" onClick={closeModal}>
                ×
              </button>
            </div>

            {error && <div className="error-banner" style={{ margin: '0 0 12px' }}>{error}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Legal name — read-only, shown only when editing and populated */}
              {editingRow && editingRow.name && (
                <label className="form-field">
                  <span className="form-label">
                    {t('suppliers.legalName')}
                    <span className="form-hint">{t('suppliers.legalNameHint')}</span>
                  </span>
                  <input value={editingRow.name} readOnly disabled />
                </label>
              )}

              {/* TIN — required at create, locked on edit */}
              <label className="form-field">
                <span className="form-label">
                  {t('suppliers.tin')}
                  {modal === 'create' && (
                    <span className="form-required">*</span>
                  )}
                  {modal !== 'create' && (
                    <span className="form-hint">{t('suppliers.tinLocked')}</span>
                  )}
                </span>
                <input
                  value={form.tin}
                  onChange={(e) => modal === 'create' && setForm({ ...form, tin: e.target.value })}
                  readOnly={modal !== 'create'}
                  disabled={modal !== 'create'}
                  placeholder="e.g. 01234567"
                  inputMode="numeric"
                  style={modal !== 'create' ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                />
              </label>

              {/* Display name */}
              <label className="form-field">
                <span className="form-label">
                  {t('suppliers.name')}
                  <span className="form-required">*</span>
                </span>
                <input
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  placeholder={t('suppliers.namePlaceholder')}
                  autoFocus={modal === 'create'}
                  onKeyDown={(e) => e.key === 'Enter' && void handleSave()}
                />
              </label>

              {/* Category */}
              <label className="form-field">
                <span className="form-label">{t('suppliers.category')}</span>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as Category })}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`suppliers.cat_${c}`)}
                    </option>
                  ))}
                </select>
              </label>

              {/* Contact person */}
              <label className="form-field">
                <span className="form-label">
                  {t('suppliers.contactPerson')}
                  <span className="form-hint">{t('common.optional')}</span>
                </span>
                <input
                  value={form.contact_person}
                  onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  placeholder={t('suppliers.contactPersonPlaceholder')}
                />
              </label>

              {/* Phone */}
              <label className="form-field">
                <span className="form-label">
                  {t('suppliers.phone')}
                  <span className="form-hint">{t('common.optional')}</span>
                </span>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder={t('suppliers.phonePlaceholder')}
                  type="tel"
                />
              </label>
            </div>

            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn-ghost" onClick={closeModal} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? '…' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
